import React, { useEffect, useRef } from 'react'

interface RecaptchaProps {
  siteKey: string
  onVerify: (token: string | null) => void
  className?: string
}

declare global {
  interface Window {
    grecaptcha: any
  }
}

const Recaptcha: React.FC<RecaptchaProps> = ({ siteKey, onVerify, className }) => {
  const recaptchaRef = useRef<HTMLDivElement>(null)
  const widgetId = useRef<number | null>(null)

  useEffect(() => {
    const scriptId = 'google-recaptcha-v2'
    let script = document.getElementById(scriptId) as HTMLScriptElement

    const renderWithRetry = (retryCount = 0) => {
      if (window.grecaptcha && window.grecaptcha.ready) {
        window.grecaptcha.ready(() => {
          if (recaptchaRef.current && widgetId.current === null) {
            try {
              widgetId.current = window.grecaptcha.render(recaptchaRef.current, {
                sitekey: siteKey,
                callback: (token: string) => onVerify(token),
                'expired-callback': () => onVerify(null),
                'error-callback': () => onVerify(null),
              })
            } catch (error) {
              console.error('reCAPTCHA render error:', error)
              if (retryCount < 3) {
                setTimeout(() => renderWithRetry(retryCount + 1), 500)
              }
            }
          }
        })
      } else if (retryCount < 10) {
        // Library not quite ready, poll for it
        setTimeout(() => renderWithRetry(retryCount + 1), 200)
      }
    }

    const handleLoad = () => renderWithRetry()

    if (!script) {
      script = document.createElement('script')
      script.id = scriptId
      script.src = 'https://www.google.com/recaptcha/api.js?render=explicit'
      script.async = true
      script.defer = true
      script.onload = handleLoad
      document.head.appendChild(script)
    } else {
      renderWithRetry()
      script.addEventListener('load', handleLoad)
    }

    return () => {
      if (script) {
        script.removeEventListener('load', handleLoad)
      }
      widgetId.current = null
    }
  }, [siteKey, onVerify])

  return <div ref={recaptchaRef} className={className} />
}

export default Recaptcha
